import React, { useEffect, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { generateShareLink, generateRecipeShareLink, isIOS, checkIfAppInstalled, getAppStoreLink, openDeepLink } from '../../utils/universalLinks';
import './SharePage.css';

const SharePage = () => {
  const { recipeId: communityId } = useParams(); // URL param name stays as recipeId for backwards compatibility
  const location = useLocation();
  const isRecipeShare = location.pathname.startsWith('/recipe/');
  const [recipeData, setRecipeData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [appInstalled, setAppInstalled] = useState(false);
  const [checkingApp, setCheckingApp] = useState(true);
  const [isCommunity, setIsCommunity] = useState(false);
  const [communityName, setCommunityName] = useState(null);
  const [communityData, setCommunityData] = useState(null);
  const [communityRecipes, setCommunityRecipes] = useState([]);
  const [isInAppBrowser, setIsInAppBrowser] = useState(false);
  const [attemptedDeepLink, setAttemptedDeepLink] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [checkedIngredients, setCheckedIngredients] = useState({});

  // Detect if we're in an in-app browser
  useEffect(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    // Check for common in-app browsers
    const inAppBrowsers = [
      'Instagram', 'FBAN', 'FBAV', // Facebook/Instagram
      'Snapchat', // Snapchat
      'TikTok', // TikTok
      'Twitter', 'LinkedIn', // Social media
      'WhatsApp', 'Telegram', // Messaging
      'Pinterest', 'Reddit' // Other social
    ];
    
    const isInApp = inAppBrowsers.some(browser => 
      userAgent.includes(browser)
    );
    
    setIsInAppBrowser(isInApp);
  }, []);

  useEffect(() => {
    const updatePageMetadata = (data) => {
      const title = data.masterRecipe?.PartitionKey || 'Recipe Details';
      const description = `Check out this recipe for ${title} on MyMenuCart`;
      
      // Update page title
      document.title = `${title} - MyMenuCart`;
      
      // Update meta tags
      updateMetaTag('description', description);
      updateMetaTag('og:title', title);
      updateMetaTag('og:description', description);
      updateMetaTag('og:url', window.location.href);
      updateMetaTag('twitter:title', title);
      updateMetaTag('twitter:description', description);
    };

    const updateCommunityMetadata = (name, description) => {
      const title = `${name} - MyMenuCart Community`;
      const desc = description || `Join ${name} on MyMenuCart to share recipes and meal plans`;
      
      document.title = title;
      updateMetaTag('description', desc);
      updateMetaTag('og:title', title);
      updateMetaTag('og:description', desc);
      updateMetaTag('og:url', window.location.href);
      updateMetaTag('twitter:title', title);
      updateMetaTag('twitter:description', desc);
    };

    const fetchCommunityData = async () => {
      try {
        // Fetch full community details with image SAS URL
        const communityResponse = await fetch(
          `https://menu-community-api.vercel.app/api/community?communityId=${communityId}&includeImageSas=true`
        );
        
        if (communityResponse.ok) {
          const data = await communityResponse.json();
          if (data.community) {
            setCommunityData(data.community);
            setCommunityName(data.community.RowKey);
            updateCommunityMetadata(data.community.RowKey, data.community.Description);
          }
        }

        // Fetch community recipes
        const recipesResponse = await fetch(
          `https://menu-community-api.vercel.app/api/community?communityId=${communityId}&getRecipes=true`
        );
        
        if (recipesResponse.ok) {
          const recipesData = await recipesResponse.json();
          if (recipesData.recipes) {
            setCommunityRecipes(recipesData.recipes);
          }
        }
      } catch (err) {
        console.error('Error fetching community data:', err);
      }
    };

    const fetchRecipe = async () => {
      try {
        setLoading(true);
        const apiParam = isRecipeShare ? 'recipeId' : 'CommunityId';
        const response = await fetch(`https://menu-api-kappa.vercel.app/api/utilities/web?${apiParam}=${communityId}`);
        if (!response.ok) {
          throw new Error('Failed to fetch recipe data');
        }
        const data = await response.json();
        if (!data) {
          throw new Error('No recipe data found');
        }
        
        // For recipe shares, always treat as recipe data
        if (isRecipeShare) {
          setRecipeData(data);
          updatePageMetadata(data);
        }
        // Check if this is a community response
        else if (data.communities && data.communities.length > 0 && data.communities[0].RowKey) {
          setIsCommunity(true);
          setCommunityName(data.communities[0].RowKey);
          // Fetch detailed community data
          await fetchCommunityData();
        } else {
          setRecipeData(data);
          // Update page metadata for better sharing
          updatePageMetadata(data);
        }
        
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (communityId) {
      fetchRecipe();
      
      // Check if app is installed (only on iOS)
      if (isIOS()) {
        checkIfAppInstalled(1500).then(installed => {
          setAppInstalled(installed);
          setCheckingApp(false);
          // Auto-redirect into the app if it's installed
          if (installed && isRecipeShare) {
            window.location.href = `mymenucart://recipe/${communityId}`;
          } else if (installed && !isRecipeShare) {
            window.location.href = `mymenucart://share/${communityId}`;
          }
        });
      } else {
        setCheckingApp(false);
      }
    }
  }, [communityId, isRecipeShare]);

  // Attempt deep link redirect on load (but only once)
  useEffect(() => {
    if (!communityId || attemptedDeepLink || !isCommunity) return;
    
    // Don't auto-redirect if in an in-app browser (user needs to see the UI first)
    if (isInAppBrowser) {
      setAttemptedDeepLink(true);
      return;
    }

    // Try to open the app automatically (works in Safari and Chrome)
    if (!checkingApp) {
      setAttemptedDeepLink(true);
      
      // Use custom scheme for non-iOS platforms
      const customScheme = `mymenucart://share/${communityId}`;
      
      console.log('🚀 Auto-attempting to open app on page load');
      
      // For iOS, use Universal Links - just redirect to the same page
      // iOS will intercept and open in app if installed
      if (isIOS()) {
        console.log('📱 iOS detected - Universal Links should open app automatically');
        // On iOS, the Universal Link association should handle this automatically
        // No need to do anything - the page itself IS the universal link
        // If we're already here and app didn't open, user doesn't have it or came from web
      } else {
        // For other platforms, try custom scheme
        console.log('🔗 Trying custom scheme:', customScheme);
        const iframe = document.createElement('iframe');
        iframe.style.border = 'none';
        iframe.style.width = '1px';
        iframe.style.height = '1px';
        iframe.style.position = 'absolute';
        iframe.style.top = '-1000px';
        iframe.src = customScheme;
        document.body.appendChild(iframe);
        
        // Clean up iframe after attempt
        setTimeout(() => {
          if (iframe.parentNode) {
            document.body.removeChild(iframe);
          }
        }, 3000);
      }
    } else {
      setAttemptedDeepLink(true);
    }
  }, [communityId, isCommunity, attemptedDeepLink, checkingApp, isInAppBrowser]);

  const updateMetaTag = (property, content) => {
    let metaTag = document.querySelector(`meta[name="${property}"]`) || 
                  document.querySelector(`meta[property="${property}"]`);
    
    if (metaTag) {
      metaTag.setAttribute('content', content);
    } else {
      metaTag = document.createElement('meta');
      metaTag.setAttribute(property.startsWith('og:') || property.startsWith('twitter:') ? 'property' : 'name', property);
      metaTag.setAttribute('content', content);
      document.head.appendChild(metaTag);
    }
  };

  const handleOpenInApp = async () => {
    const path = isRecipeShare ? `recipe/${communityId}` : `share/${communityId}`;
    const universalLink = `https://mymenucart.com/${path}`;
    const customSchemeUrl = `mymenucart://${path}`;
    const fallbackUrl = getAppStoreLink();

    if (isIOS()) {
      // On iOS use Universal Links (HTTPS) — iOS intercepts and opens the app if installed
      window.location.href = universalLink;
      // Fallback to App Store if the app isn't installed
      setTimeout(() => {
        if (!document.hidden) {
          window.location.href = fallbackUrl;
        }
      }, 2000);
    } else {
      // For non-iOS, try custom scheme with fallback
      const opened = await openDeepLink(customSchemeUrl, fallbackUrl, 3000);
      if (!opened) {
        console.log('📲 Redirecting to App Store');
      }
    }
  };

  const handleJoinCommunity = () => {
    // Show modal explaining they need the app
    setShowJoinModal(true);
  };

  const handleJoinConfirm = () => {
    setShowJoinModal(false);
    // Open the app (which will trigger the join flow in the app)
    handleOpenInApp();
  };

  const handleShare = async () => {
    const title = recipeData.masterRecipe?.PartitionKey || 'Recipe from MyMenuCart';
    const text = `Check out this recipe: ${title}`;
    const url = isRecipeShare ? generateRecipeShareLink(communityId) : generateShareLink(communityId);
    const shareData = { title, text, url };

    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        console.error('Error sharing:', error);
        fallbackShare(shareData.url);
      }
    } else {
      fallbackShare(shareData.url);
    }
  };

  const fallbackShare = (url) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        alert('Recipe link copied to clipboard!');
      }).catch(() => {
        promptToCopyLink(url);
      });
    } else {
      promptToCopyLink(url);
    }
  };

  const promptToCopyLink = (url) => {
    const textArea = document.createElement('textarea');
    textArea.value = url;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      alert('Recipe link copied to clipboard!');
    } catch (error) {
      alert(`Copy this link: ${url}`);
    }
    document.body.removeChild(textArea);
  };

  if (loading) {
    return (
      <div className="share-page-loading">
        <div className="loading-spinner"></div>
        <p>{isRecipeShare ? 'Loading recipe...' : 'Loading recipe details...'}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="share-page-error">
        <h2>{isRecipeShare ? 'Recipe Not Found' : 'Community Not Found'}</h2>
        <p>{isRecipeShare 
          ? 'This recipe may have been removed or is only available in the MyMenuCart app.' 
          : 'This community might be better viewed in the MyMenuCart app.'}
        </p>
        <div className="error-actions">
          <button className="community-button primary" onClick={handleOpenInApp}>
            {appInstalled ? 'Open in MyMenuCart' : 'Get MyMenuCart App'}
          </button>
        </div>
      </div>
    );
  }

  // If this is a community, show special community page
  if (isCommunity && communityName) {
    return (
      <div className="share-page-container">
        <div className="community-found-container">
          {/* Community Header with Image */}
          <div className="community-header">
            {communityData?.ImageSasUrl ? (
              <div className="community-header-image">
                <img 
                  src={communityData.ImageSasUrl} 
                  alt={communityName}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="community-header-fallback" style={{ display: 'none' }}>
                  <img src="/myMenuCartIcon.png" alt="MyMenuCart" className="community-app-icon-small" />
                </div>
              </div>
            ) : (
              <div className="community-header-fallback">
                <img src="/myMenuCartIcon.png" alt="MyMenuCart" className="community-app-icon" />
              </div>
            )}
            
            <h1>{communityName}</h1>
            
            {communityData?.MemberCount && (
              <p className="community-member-count">
                {communityData.MemberCount} {communityData.MemberCount === 1 ? 'member' : 'members'}
              </p>
            )}
            
            {isInAppBrowser && (
              <div className="in-app-browser-notice">
                <p>📱 Opened in app browser - Tap the <span className="external-link-icon">↗</span> icon to open the app</p>
              </div>
            )}
          </div>

          {/* Community Description */}
          {communityData?.Description && (
            <div className="community-description">
              <p>{communityData.Description}</p>
            </div>
          )}

          {/* Sample Recipes */}
          {communityRecipes.length > 0 && (
            <div className="community-recipes-preview">
              <h2>Recipes in this community</h2>
              <div className="recipe-grid">
                {communityRecipes.slice(0, 6).map((recipe, index) => (
                  <div key={recipe.recipeId || index} className="recipe-card">
                    {recipe.url ? (
                      <div className="recipe-card-image">
                        <img 
                          src={recipe.url} 
                          alt={recipe.recipeName}
                          onError={(e) => {
                            e.target.src = '/myMenuCartIcon.png';
                          }}
                        />
                      </div>
                    ) : (
                      <div className="recipe-card-placeholder">
                        <span>🍽️</span>
                      </div>
                    )}
                    <div className="recipe-card-info">
                      <h3>{recipe.recipeName}</h3>
                      {recipe.classification && (
                        <p className="recipe-classification">{recipe.classification}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {communityRecipes.length > 6 && (
                <p className="more-recipes-notice">
                  +{communityRecipes.length - 6} more {communityRecipes.length - 6 === 1 ? 'recipe' : 'recipes'} in the app
                </p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="community-actions">
            <button 
              className="community-button primary"
              onClick={handleJoinCommunity}
            >
              Join Community
            </button>
            
            <button 
              className="community-button secondary"
              onClick={handleOpenInApp}
            >
              {isIOS() 
                ? (appInstalled ? 'Open App' : 'Download App')
                : (appInstalled ? 'Open in MyMenuCart' : 'Get MyMenuCart App')
              }
            </button>
          </div>

          {/* Join Modal */}
          {showJoinModal && (
            <div className="modal-overlay" onClick={() => setShowJoinModal(false)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Join {communityName}?</h2>
                  <button className="modal-close" onClick={() => setShowJoinModal(false)}>
                    ×
                  </button>
                </div>
                <div className="modal-body">
                  <div className="modal-icon">📱</div>
                  <p className="modal-message">
                    To join this community, you'll need the MyMenuCart app.
                  </p>
                  <p className="modal-submessage">
                    {appInstalled 
                      ? "We'll open the app where you can complete joining this community."
                      : "We'll take you to the App Store to download MyMenuCart, then you can join this community."}
                  </p>
                </div>
                <div className="modal-actions">
                  <button 
                    className="modal-button primary"
                    onClick={handleJoinConfirm}
                  >
                    {appInstalled ? 'Open App' : 'Get App & Join'}
                  </button>
                  <button 
                    className="modal-button secondary"
                    onClick={() => setShowJoinModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Features List */}
          <div className="community-features">
            <h2>What you can do in the app:</h2>
            <ul>
              <li>View all community recipes and meal plans</li>
              <li>Share your own recipes with the community</li>
              <li>Collaborate and cook together</li>
              <li>Access shopping lists and meal planning tools</li>
              <li>Get notified of new recipes and updates</li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // Parse instructions from JSON string
  const parsedInstructions = recipeData.masterRecipe?.Instructions
    ? JSON.parse(recipeData.masterRecipe.Instructions).instructions || []
    : [];

  const toggleIngredient = (index) => {
    setCheckedIngredients(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const recipeName = recipeData.masterRecipe?.PartitionKey || 'Recipe Details';

  return (
    <div className="share-page-container">
      {/* iOS App Banner - Only show on iOS */}
      {isIOS() && !checkingApp && (
        <div className="ios-app-banner">
          <div className="ios-app-banner-content">
            <img src="/myMenuCartIcon.png" alt="MyMenuCart" className="ios-app-icon" />
            <div className="ios-app-info">
              <div className="ios-app-title">MyMenuCart</div>
              <div className="ios-app-subtitle">
                {appInstalled ? 'Open in app for the best experience' : 'Get the app for the best experience'}
              </div>
            </div>
            <button 
              className="ios-app-button"
              onClick={handleOpenInApp}
            >
              {appInstalled ? 'OPEN' : 'GET'}
            </button>
          </div>
        </div>
      )}

      {/* Non-iOS App Prompt */}
      {!isIOS() && isRecipeShare && (
        <div className="web-app-prompt">
          <img src="/myMenuCartIcon.png" alt="MyMenuCart" className="web-app-prompt-icon" />
          <div className="web-app-prompt-text">
            <span className="web-app-prompt-title">View in MyMenuCart</span>
            <span className="web-app-prompt-subtitle">Save recipes, plan meals & build shopping lists</span>
          </div>
          <a href={getAppStoreLink()} className="web-app-prompt-button" target="_blank" rel="noopener noreferrer">
            Get App
          </a>
        </div>
      )}

      {recipeData.masterRecipe?.ImageUrl && (
        <div className="recipe-image-container">
          <img
            src={recipeData.masterRecipe.ImageUrl}
            alt={recipeName}
            className="recipe-image"
          />
        </div>
      )}

      <div className="recipe-hero">
        <div className="recipe-hero-content">
          <h1 className="recipe-title">{recipeName}</h1>
          
          <div className="recipe-meta">
            {recipeData.masterRecipe?.Time && (
              <div className="recipe-meta-item">
                <span className="recipe-meta-icon">🕐</span>
                <span className="recipe-meta-label">Time</span>
                <span className="recipe-meta-value">{recipeData.masterRecipe.Time}</span>
              </div>
            )}
            {recipeData.ingredients && recipeData.ingredients.length > 0 && (
              <div className="recipe-meta-item">
                <span className="recipe-meta-icon">🥘</span>
                <span className="recipe-meta-label">Ingredients</span>
                <span className="recipe-meta-value">{recipeData.ingredients.length}</span>
              </div>
            )}
            {parsedInstructions.length > 0 && (
              <div className="recipe-meta-item">
                <span className="recipe-meta-icon">📝</span>
                <span className="recipe-meta-label">Steps</span>
                <span className="recipe-meta-value">{parsedInstructions.length}</span>
              </div>
            )}
          </div>

          {recipeData.masterRecipe?.URL && recipeData.masterRecipe.URL !== 'Manual' && (
            <div className="recipe-source">
              Source: <a href={recipeData.masterRecipe.URL} target="_blank" rel="noopener noreferrer">
                {recipeData.masterRecipe.Title !== 'Manual' && recipeData.masterRecipe.Title !== 'Generated'
                  ? recipeData.masterRecipe.Title
                  : 'Original Recipe'}
              </a>
            </div>
          )}
        </div>

        <div className="recipe-hero-actions">
          <button className="share-button" onClick={handleShare}>
            <span className="share-button-icon">↗</span> Share
          </button>
          <button className="open-app-button" onClick={handleOpenInApp}>
            {appInstalled ? 'Open in App' : 'Get the App'}
          </button>
        </div>
      </div>

      {recipeData.ingredients && recipeData.ingredients.length > 0 && (
        <div className="recipe-ingredients-section">
          <div className="section-header">
            <h2>Ingredients</h2>
          </div>
          <ul className="recipe-ingredient-list">
            {recipeData.ingredients.map((ingredient, index) => {
              const ingredientText = ingredient.Original
                || (ingredient.Unit && ingredient.Unit !== ""
                  ? `${ingredient.Amount} ${ingredient.Unit} ${ingredient.Ingredient}`
                  : `${ingredient.Amount} ${ingredient.Ingredient}`);

              return (
                <li 
                  key={index} 
                  className={`recipe-ingredient-item ${checkedIngredients[index] ? 'checked' : ''}`}
                  onClick={() => toggleIngredient(index)}
                >
                  <span className="ingredient-checkbox">
                    {checkedIngredients[index] ? '✓' : ''}
                  </span>
                  <span className="ingredient-text">{ingredientText}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {parsedInstructions.length > 0 && (
        <div className="recipe-directions-section">
          <h2>Directions</h2>
          <ol className="recipe-direction-list">
            {parsedInstructions.map((instruction, index) => (
              <li key={index} className="recipe-direction-item">
                <div className="direction-step-number">Step {index + 1}</div>
                <div className="direction-step-text">{instruction}</div>
              </li>
            ))}
          </ol>
        </div>
      )}

      {(!recipeData.ingredients || recipeData.ingredients.length === 0) && 
       parsedInstructions.length === 0 && (
        <div className="recipe-empty-state">
          <div className="empty-state-icon">📖</div>
          <p>This recipe has more details available in the MyMenuCart app.</p>
          <button className="community-button primary" onClick={handleOpenInApp}>
            {appInstalled ? 'Open in MyMenuCart' : 'Get MyMenuCart App'}
          </button>
        </div>
      )}

      {/* Bottom CTA for recipe shares */}
      {isRecipeShare && (recipeData.ingredients?.length > 0 || parsedInstructions.length > 0) && (
        <div className="recipe-bottom-cta">
          <div className="recipe-bottom-cta-content">
            <img src="/myMenuCartIcon.png" alt="MyMenuCart" className="bottom-cta-icon" />
            <div className="bottom-cta-text">
              <strong>Want to save this recipe?</strong>
              <span>Add it to your meal plan and shopping list in MyMenuCart</span>
            </div>
          </div>
          <button className="community-button primary" onClick={handleOpenInApp}>
            {appInstalled ? 'Open in MyMenuCart' : 'Get MyMenuCart — It\'s Free'}
          </button>
        </div>
      )}
    </div>
  );
};

export default SharePage;
